import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ShieldCheck, CreditCard } from "@phosphor-icons/react";
import { LoginAuthContext } from "Context/Authentication/LoginAuth";
import useApiQuery from "Hooks/useApiQuery";
import Container from "Components/Container/Container";

const HomeBanner = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, isVerified, isAuthenticated } = useContext(LoginAuthContext);
  const [dismissed, setDismissed] = useState(false);
  const isRTL = i18n.language === "ar";

  const { data } = useApiQuery("home/home-banner");
  const banner = data?.data;

  // Don't show if: not logged in, fully authenticated, dismissed, or no banner data
  if (!isLoggedIn || isAuthenticated || dismissed || !banner || !banner.isActive) {
    return null;
  }

  // Determine which banner to show
  const isUnverified = !isVerified;
  const title = isUnverified ? banner.unverified_title : banner.no_plan_title;
  const text = isUnverified ? banner.unverified_text : banner.no_plan_text;
  const features = isUnverified ? [] : (banner.no_plan_features || []);
  const ctaPath = isUnverified ? "/verify-email" : "/plans";
  const ctaLabel = isUnverified
    ? (t("general.continue_setup") || "Continue Setup")
    : (t("general.choose_plan") || "Choose Plan");
  const Icon = isUnverified ? ShieldCheck : CreditCard;

  if (!title && !text) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4 }}
      >
        <Container>
          <div className="relative bg-gradient-to-r from-primary/20 via-primary/10 to-transparent border border-primary/30 rounded-2xl p-5 lg:p-6 mt-8">
            {/* Dismiss */}
            <button
              onClick={() => setDismissed(true)}
              className="absolute top-3 end-3 text-gray-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              {/* Icon */}
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon size={24} className="text-primary" />
              </div>

              {/* Content */}
              <div className="flex-1">
                {title && (
                  <h3 className="text-lg font-bold text-white mb-1">{title}</h3>
                )}
                {text && (
                  <p className="text-gray-400 text-sm mb-0">{text}</p>
                )}
                {features.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-3">
                    {features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-white/10 text-gray-300 px-3 py-1 rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* CTA */}
              <button
                onClick={() => navigate(ctaPath)}
                className="bg-primary hover:bg-primary/90 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl text-sm font-semibold
                  transition-all duration-300 flex items-center gap-2 flex-shrink-0 min-h-[44px]"
              >
                {ctaLabel}
                <ArrowRight size={16} className={isRTL ? "rotate-180" : ""} />
              </button>
            </div>
          </div>
        </Container>
      </motion.div>
    </AnimatePresence>
  );
};

export default HomeBanner;
